import clsx from "clsx"
import { motion } from "framer-motion"
import type { Variants } from "framer-motion"
import React, { useContext } from "react"

import PageThemeContext from "../page-theme/PageThemeContext"

interface IHamburgerProps {
  navOpen: boolean
  className?: string
  hamburgerClick?: React.MouseEventHandler
}

const Hamburger: React.FC<IHamburgerProps> = ({
  navOpen,
  className,
  hamburgerClick,
}) => {
  // For the open and close animation states for each bar in the hamburger we use translateY not y because:
  // For SVG components, x and y are positional attributes distinct from translateX and translateY.
  // Using the former will change the element's x/y attributes,
  // while using the latter will set its style.transform property.
  // Also note the 1.2px, in the animationVariants const below is half of the height of each svg rect
  // used for the top and bottom bars of the hamburger.
  // This 1.2px gets subtracted or added, depending on if it's the bottom bar moving up or top bar moving down,
  // in order to animate to the vertical center of the hamburger.
  // If you don't do this then the final position of the bars after open animation will be
  // 50% plus the value of the height of the bar, in which case they will not be centered.
  const animationVariants: Variants = {
    topAnimation: {
      translateY: navOpen ? "calc(50% - 1.2px)" : "0%",
      rotate: navOpen ? 45 : 0,
    },
    centerAnimation: {
      opacity: navOpen ? 0 : 1,
      rotate: navOpen ? 45 : 0,
    },
    bottomAnimation: {
      translateY: navOpen ? "calc(-50% + 1.2px)" : "0%",
      rotate: navOpen ? -45 : 0,
    },
  }

  // define const for the transition style for less code */
  const animationSpring = {
    type: "tween" as const,
    duration: 0.3,
  }

  const pageThemeContext = useContext(PageThemeContext)

  return (
    <button
      type="button"
      onClick={hamburgerClick}
      className={clsx(
        className,
        "relative w-6 rounded-0.3875rem overflow-hidden cursor-pointer",
      )}
      name="toggle-menu"
      aria-label="Menu Toggle Button"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 19"
        fill={pageThemeContext.pageTheme.dark ? "#fafafa" : "#141a1f"}
      >
        <motion.rect
          id="top"
          width="24"
          height="2.41"
          variants={animationVariants}
          transition={animationSpring}
          animate="topAnimation"
        />
        <motion.rect
          id="center"
          y="8.3"
          width="24"
          height="2.43"
          variants={animationVariants}
          transition={animationSpring}
          animate="centerAnimation"
        />
        <motion.rect
          id="bottom"
          y="16.6"
          width="24"
          height="2.4"
          variants={animationVariants}
          transition={animationSpring}
          animate="bottomAnimation"
        />
      </svg>
    </button>
  )
}

export default Hamburger
